import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalculatorIcon from '@mui/icons-material/Calculate';
import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiClientRaw } from '../../globals/utils/api';
import PremiumCalculator from '../SHARED/PremiumCalculatorPublic';
import SupportContactDialog from '../../components/SupportContactDialog';

const InternalUserRegister = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [openCalculator, setOpenCalculator] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    username: '',
    password: '',
    confirmPassword: '',
  });

  // Error states for inline validation
  const [usernameError, setUsernameError] = useState('');
  const [nameError, setNameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const primaryColor = '#333333';
  const secondaryColor = '#666666';

  const validateUsername = () => {
    if (!formData.username.trim()) {
      setUsernameError('Username is required');
      return false;
    }
    const isEmailFormat = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.username);
    if (isEmailFormat) {
      setUsernameError('Username cannot be an email address');
      return false;
    }
    setUsernameError('');
    return true;
  };

  const validateName = () => {
    if (!formData.name.trim()) {
      setNameError('Full name is required');
      return false;
    }
    setNameError('');
    return true;
  };

  const validatePassword = () => {
    if (!formData.password.trim()) {
      setPasswordError('Password is required');
      return false;
    }
    if (formData.password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return false;
    }
    if (!/\d/.test(formData.password)) {
      setPasswordError('Password must contain at least one number');
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      setPasswordError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setPasswordError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      setPasswordError('Password must contain at least one special character');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = () => {
    if (!formData.confirmPassword.trim()) {
      setConfirmPasswordError('Please confirm your password');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when user starts typing
    switch (name) {
      case 'username':
        if (usernameError) setUsernameError('');
        break;
      case 'name':
        if (nameError) setNameError('');
        break;
      case 'password':
        if (passwordError) setPasswordError('');
        break;
      case 'confirmPassword':
        if (confirmPasswordError) setConfirmPasswordError('');
        break;
      default:
        break;
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();

    if (!validateUsername() || !validateName() || !validatePassword() || !validateConfirmPassword()) {
      return;
    }

    setLoading(true);
  
    try {
      const response = await apiClientRaw(`/register-internal-user/${token}`, "POST", {
        user: {
          username: formData.username,
          password: formData.password,
        },
        full_name: formData.name,
      });
  
      if (response && response.status >= 200 && response.status < 300) {
        toast.success("Internal User registered successfully");
        navigate('/');
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        if (errorData.user) {
          if (errorData.user.username) {
            errorData.user.username.forEach((msg) => toast.error(`Invalid Username: ${msg==='vermost portal user with this username already exists.'?'Username already taken. Please try another one':msg}`));
          }
          if (errorData.user.password) {
            errorData.user.password.forEach((msg) => toast.error(`Invalid Password: ${msg}`));
          }
        }
        if (errorData.fullname) {
          errorData.fullname.forEach((msg) => toast.error(`Full Name: ${msg}`));
        }
      } else {
        toast.error("An error occurred during registration. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') handleRegister(event);
  };

  return (
    <Box
      display="flex"
      flexDirection="row"
      minHeight="100vh"
      sx={{ 
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Left Column - Hero Section */}
      <Box
        sx={{
          width: isMobile ? '0%' : '60%',
          display: isMobile ? 'none' : 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%, #000000 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 30%, rgba(51, 51, 51, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none'
          },
          '@media (max-width: 768px)': {
            '&::before': {
              background: 'transparent'
            }
          }
        }}
      >
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: '15%',
          left: '10%',
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(51, 51, 51, 0.3), rgba(102, 102, 102, 0.1))',
          filter: 'blur(40px)',
          animation: 'float 6s ease-in-out infinite'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '25%',
          right: '10%',
          width: '180px',
          height: '180px',
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(51, 51, 51, 0.2), rgba(102, 102, 102, 0.05))',
          filter: 'blur(60px)',
          animation: 'float 8s ease-in-out infinite reverse'
        }} />
        
        {/* Logo */}
        <Box
          component="img"
          src="/LogoNew.png"
          alt="Company Logo"
          sx={{
            position: 'absolute',
            top: 40,
            left: 40,
            width: '120px',
            height: 'auto',
            zIndex: 10,
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))'
          }}
        />

        {/* Darren Image - Main Focus */}
        <Box sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(0,0,0,0.3) 0%, transparent 50%, rgba(51, 51, 51, 0.1) 100%)',
            zIndex: 1
          }
        }}>
          <Box 
            component="img" 
            src="/DarrenImage.png" 
            alt="Darren Vermost" 
            sx={{ 
              maxHeight: '85vh',
              maxWidth: '90%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.8), 0 0 40px rgba(51, 51, 51, 0.2)',
              border: '2px solid rgba(51, 51, 51, 0.3)',
              zIndex: 2,
              filter: 'contrast(1.1) brightness(1.05)'
            }} 
          />
        </Box>
      </Box>

      {/* Right Column - Registration Form */}
      <Box
        sx={{
          width: isMobile ? '100%' : '40%',
          display: 'flex',
          paddingY: '8vh',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#fafafa',
          position: 'relative',
          minHeight: '100vh',
          background: isMobile ? 
            'white' :
            'linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)',
          '&::before': isMobile ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'transparent',
            pointerEvents: 'none'
          } : {}
        }}
      >
        {/* Registration Form Container */}
        <Box
          sx={{
            width: '90%',
            maxWidth: '480px',
            backgroundColor: 'transparent',
            p: { xs: 3, sm: 4 },
            position: 'relative'
          }}
        >
          {/* Company Logo */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box 
              component="img" 
              src="/ProbateBondPros.png" 
              alt="Probate Bond Pros Logo" 
              sx={{ 
                height: 60, 
                maxWidth: '100%',
                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.2))'
              }} 
            />
          </Box>

          {/* Form Header */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Box sx={{ 
              position: 'absolute', 
              top: 40, 
              left: 0,
              '@media (max-width: 768px)': {
                left: -10
              }
            }}>
              <IconButton 
                onClick={() => navigate(-1)} 
                sx={{ 
                  color: primaryColor,
                  backgroundColor: 'rgba(51, 51, 51, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(51, 51, 51, 0.2)'
                  }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Welcome Section */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h4" sx={{ 
              fontWeight: 700, 
              color: primaryColor, 
              mb: 0.5,
              fontSize: { xs: '1.6rem', sm: '1.8rem' }
            }}>
              Create Internal User Account
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              fontSize: '1rem',
              fontWeight: 400
            }}>
              Please fill out the form to register
            </Typography>
          </Box>

          {/* Important Info Banner */}
          <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            background: 'linear-gradient(135deg, #FFFBF0 0%, #F8F0FF 100%)',
            color: '#6B4F1D',
            borderRadius: '12px',
            p: 3,
            mb: 3,
            boxShadow: '0 8px 32px rgba(51, 51, 51, 0.1)',
            border: '1px solid rgba(51, 51, 51, 0.2)',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '4px',
              height: '100%',
              background: `linear-gradient(to bottom, ${primaryColor}, ${secondaryColor})`,
              borderRadius: '12px 0 0 12px'
            }
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              minWidth: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'rgba(51, 51, 51, 0.1)',
              mr: 3,
              flexShrink: 0
            }}>
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="12" fill="#333333"/>
                <path d="M12 7v5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="12" cy="16" r="1" fill="#FFFFFF"/>
              </svg>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                mb: 1,
                fontSize: '1.1rem',
                color: '#333333'
              }}>
                Important Registration Information
              </Typography>
              <Typography variant="body2" sx={{ 
                color: '#333333',
                lineHeight: 1.6,
                fontSize: '0.95rem'
              }}>
                Please remember your <strong style={{ color: '#666666' }}>username</strong> as it will be used for login.
                <br />
                <span style={{ color: '#666666', fontWeight: 600 }}>Username cannot be an email address.</span>
              </Typography>
            </Box>
          </Box>

          {/* Form Fields */}
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Username
              </Typography>
              <Tooltip 
                title="Enter a unique username (cannot be an email address)"
                placement="top"
                arrow
              >
                <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              name="username"
              required
              value={formData.username}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={validateUsername}
              placeholder="Enter your username here"
              error={!!usernameError}
              helperText={usernameError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0px',
                  backgroundColor: 'rgba(249, 249, 249, 0.8)',
                  '& fieldset': {
                    borderColor: usernameError ? '#d32f2f' : '#E0E0E0',
                    borderWidth: '2px'
                  },
                  '&:hover fieldset': {
                    borderColor: usernameError ? '#d32f2f' : primaryColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: usernameError ? '#d32f2f' : primaryColor,
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'text.secondary',
                  opacity: 0.7
                },
                '& .MuiFormHelperText-root': {
                  margin: '4px 0 0 0',
                  fontSize: '0.75rem'
                }
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Full Name
              </Typography>
              <Tooltip 
                title="Enter your full legal name"
                placement="top"
                arrow
              >
                <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              name="name"
              required
              value={formData.name}
              onChange={handleInputChange}
              onBlur={validateName}
              placeholder="Enter your full name here"
              error={!!nameError}
              helperText={nameError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0px',
                  backgroundColor: 'rgba(249, 249, 249, 0.8)',
                  '& fieldset': {
                    borderColor: nameError ? '#d32f2f' : '#E0E0E0',
                    borderWidth: '2px'
                  },
                  '&:hover fieldset': {
                    borderColor: nameError ? '#d32f2f' : primaryColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: nameError ? '#d32f2f' : primaryColor,
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'text.secondary',
                  opacity: 0.7
                },
                '& .MuiFormHelperText-root': {
                  margin: '4px 0 0 0',
                  fontSize: '0.75rem'
                }
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Password
              </Typography>
              <Tooltip 
                title="Enter a strong password with at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character"
                placement="top"
                arrow
              >
                <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              name="password"
              type={showPassword ? 'text' : 'password'}
              required
              value={formData.password}
              onChange={handleInputChange}
              onBlur={validatePassword}
              placeholder="Enter your password here"
              error={!!passwordError}
              helperText={passwordError || (formData.password ? 'Password must be 8 characters, one uppercase, one lowercase, one number and one special character' : '')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0px',
                  backgroundColor: 'rgba(249, 249, 249, 0.8)',
                  '& fieldset': {
                    borderColor: passwordError ? '#d32f2f' : '#E0E0E0',
                    borderWidth: '2px'
                  },
                  '&:hover fieldset': {
                    borderColor: passwordError ? '#d32f2f' : primaryColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: passwordError ? '#d32f2f' : primaryColor,
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'text.secondary',
                  opacity: 0.7
                },
                '& .MuiFormHelperText-root': {
                  margin: '4px 0 0 0',
                  fontSize: '0.75rem',
                  color: passwordError ? '#d32f2f' : 'text.secondary'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                      sx={{ color: 'text.secondary' }}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Confirm Password
              </Typography>
              <Tooltip 
                title="Confirm your password by entering it again"
                placement="top"
                arrow
              >
                <IconButton size="small" sx={{ color: 'text.secondary', p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              required
              value={formData.confirmPassword}
              onChange={handleInputChange}
              onBlur={validateConfirmPassword}
              placeholder="Confirm your password here"
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0px',
                  backgroundColor: 'rgba(249, 249, 249, 0.8)',
                  '& fieldset': {
                    borderColor: confirmPasswordError ? '#d32f2f' : '#E0E0E0',
                    borderWidth: '2px'
                  },
                  '&:hover fieldset': {
                    borderColor: confirmPasswordError ? '#d32f2f' : primaryColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: confirmPasswordError ? '#d32f2f' : primaryColor,
                  }
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'text.secondary',
                  opacity: 0.7
                },
                '& .MuiFormHelperText-root': {
                  margin: '4px 0 0 0',
                  fontSize: '0.75rem'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                      sx={{ color: 'text.secondary' }}
                    >
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Registration Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
              <Button 
                variant="contained" 
                disabled={loading}
                onClick={handleRegister}
                sx={{ 
                  borderRadius: '0px', 
                  py: 1.2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  bgcolor: primaryColor, 
                  color: 'white',
                  boxShadow: '0 8px 32px rgba(51, 51, 51, 0.3)',
                  '&:hover': { 
                    bgcolor: secondaryColor,
                    boxShadow: '0 12px 40px rgba(51, 51, 51, 0.4)',
                    transform: 'translateY(-1px)'
                  }, 
                  textTransform: 'none',
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'REGISTER'}
              </Button>
              
              <Button 
                variant="outlined" 
                onClick={() => navigate('/login')}
                sx={{ 
                  borderRadius: '0px', 
                  py: 1,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  borderColor: primaryColor, 
                  color: primaryColor, 
                  borderWidth: '2px',
                  '&:hover': { 
                    borderColor: secondaryColor, 
                    backgroundColor: 'rgba(51, 51, 51, 0.08)',
                    borderWidth: '2px'
                  },
                  textTransform: 'none'
                }}
              >
                Back to Login
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Calculator Tab at Bottom */}
      <Box sx={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0,
        right: 0,
        zIndex: 1000,
        display: 'flex', 
        justifyContent: 'flex-end',
        alignItems: 'center',
        pr: 2,
        gap: 1,
        flexWrap: 'wrap'
      }}>
        {/* Support Tab */}
        <Box
          onClick={() => setOpenSupport(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            backgroundColor: '#D3D3D3',
            color: '#333',
            px: 4,
            py: 2,
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            minWidth: '300px',
            maxWidth: '400px',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#C8C8C8',
              transform: 'translateY(-2px)',
              boxShadow: '0 -6px 16px rgba(0, 0, 0, 0.2)'
            },
            '@media (max-width: 1024px)': {
              minWidth: '260px',
              maxWidth: '340px',
              px: 3,
              py: 1.5,
              gap: 1.5
            },
            '@media (max-width: 768px)': {
              minWidth: '220px',
              maxWidth: '300px',
              px: 2,
              py: 1,
              gap: 1
            }
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: '#333',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '2px',
              flexShrink: 0,
              '@media (max-width: 1024px)': {
                width: 18,
                height: 18
              },
              '@media (max-width: 768px)': {
                width: 16,
                height: 16
              }
            }}
          >
            <EmailIcon sx={{ 
              fontSize: 14, 
              color: 'white',
              '@media (max-width: 1024px)': {
                fontSize: 12
              },
              '@media (max-width: 768px)': {
                fontSize: 10
              }
            }} />
          </Box>
          <Typography variant="body1" sx={{ 
            fontSize: '1rem', 
            fontWeight: 500,
            color: '#333',
            whiteSpace: 'nowrap',
            '@media (max-width: 1024px)': {
              fontSize: '0.9rem'
            },
            '@media (max-width: 768px)': {
              fontSize: '0.8rem'
            }
          }}>
            Contact Support
          </Typography>
        </Box>

        <Box
          onClick={() => setOpenCalculator(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            backgroundColor: '#D3D3D3',
            color: '#333',
            px: 4,
            py: 2,
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            minWidth: '320px',
            maxWidth: '400px',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#C8C8C8',
              transform: 'translateY(-2px)',
              boxShadow: '0 -6px 16px rgba(0, 0, 0, 0.2)'
            },
            '@media (max-width: 1024px)': {
              minWidth: '280px',
              maxWidth: '350px',
              px: 3,
              py: 1.5,
              gap: 1.5
            },
            '@media (max-width: 768px)': {
              minWidth: '240px',
              maxWidth: '300px',
              px: 2,
              py: 1,
              gap: 1
            }
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: '#333',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '2px',
              flexShrink: 0,
              '@media (max-width: 1024px)': {
                width: 18,
                height: 18
              },
              '@media (max-width: 768px)': {
                width: 16,
                height: 16
              }
            }}
          >
            <CalculatorIcon sx={{ 
              fontSize: 14, 
              color: 'white',
              '@media (max-width: 1024px)': {
                fontSize: 12
              },
              '@media (max-width: 768px)': {
                fontSize: 10
              }
            }} />
          </Box>
          <Typography variant="body1" sx={{ 
            fontSize: '1rem', 
            fontWeight: 500,
            color: '#333',
            whiteSpace: 'nowrap',
            '@media (max-width: 1024px)': {
              fontSize: '0.9rem'
            },
            '@media (max-width: 768px)': {
              fontSize: '0.8rem'
            }
          }}>
            Use our Bond Premium Calculator
          </Typography>
        </Box>
      </Box>

      <SupportContactDialog
        open={openSupport}
        onClose={() => setOpenSupport(false)}
        context="Internal User Register"
      />

      <Dialog 
        open={openCalculator} 
        onClose={() => setOpenCalculator(false)} 
        sx={{ 
          '& .MuiPaper-root': { 
            borderRadius: '20px',
            maxWidth: '90vw'
          } 
        }}
      >
        <DialogContent>
          <PremiumCalculator />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenCalculator(false)}
            sx={{
              borderRadius: '12px',
              color: primaryColor,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(51, 51, 51, 0.08)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </Box>
  );
};

export default InternalUserRegister;